import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Heart, Loader2, Settings, Share2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../components/ui/use-toast';
import LocationHeader from '../components/location/LocationHeader';
import SponsorsStrip from '../components/location/SponsorsStrip';
import GymStaffActions from '../components/gym/GymStaffActions';
import FollowersList from '../components/FollowersList';
import StageEntryDialogs from '../components/trophy/StageEntryDialogs';
import { useGuardianWards } from '../components/guardian/useGuardianWards';
import TrophyParticipationBanner from '../components/trophy/TrophyParticipationBanner';
import TrophyStagesList from '../components/trophy/TrophyStagesList';
import { useResumeStageEntry, useStageEntry, withStageStanding } from '../components/trophy/useStageEntry';
import { trophiesService } from '../services/trophies';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { getGymAdminBaseUrl } from '../utils/gym-admin-url';
import { trophyBadge } from '../utils/lifecycle-status';
import type { StageParticipation, Trophy } from '../types/trophy';

// Shared styling for icon buttons overlaid on the cover image, as on gym-profile.
const COVER_OVERLAY_BUTTON =
  'h-9 w-9 bg-black/40 border-white/20 text-white hover:bg-black/60 hover:text-white';

/**
 * A circuit's public page. The header is a gym's, verbatim — the same
 * `LocationHeader` with the same full-bleed treatment — because a trophy has
 * exactly what that header shows: a cover carousel, a logo, a name and a
 * description.
 *
 * Below it, where a gym lists its walls, a circuit lists its stages.
 */
export default function TrophyProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();

  const [trophy, setTrophy] = useState<Trophy | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Entering a stage from here: the row it came from is redrawn as entered,
  // and the stage's entrant count moves with it.
  const onEntered = useCallback(
    (standing: StageParticipation) =>
      setTrophy((current) =>
        current
          ? {
              ...current,
              participation: withStageStanding(current.participation, standing),
              stages: current.stages.map((stage) =>
                stage.id === standing.id
                  ? { ...stage, participants_count: stage.participants_count + 1 }
                  : stage
              ),
            }
          : current
      ),
    []
  );
  // Read once for the page: the stage cards draw the children entered in each,
  // and the picker behind the button works off the same list.
  const { wards, reload: reloadWards } = useGuardianWards();
  const entry = useStageEntry(onEntered, { wards, onWardsChanged: reloadWards });
  // Back from signing up to enter one of the stages: the dialog opens on it.
  useResumeStageEntry(entry.requestEnter, trophy?.participation?.stages);

  // Staff of the circuit itself. A stage's staff is not the trophy's, so this
  // deliberately matches on the trophy's own slug and nothing else.
  const currentUserStaff = user?.staff?.find((staff) => staff.gym_slug === slug) ?? null;
  const isStaffAdmin =
    currentUserStaff?.status === 'confirmed' && currentUserStaff.staff_roles.includes('admin');

  useEffect(() => {
    if (!slug) return;

    let isMounted = true;
    setLoading(true);

    trophiesService
      .getTrophyBySlug(slug)
      .then((data) => {
        if (!isMounted) return;
        setTrophy(data);
        setIsFollowing(data.is_following ?? false);
      })
      .catch((error) => {
        if (!isMounted) return;
        setTrophy(null);
        toast({
          title: t('trophyProfile.failedToLoad'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [slug, t, toast]);

  const toggleFollow = useCallback(async () => {
    if (!slug) return;
    if (!user) {
      toast({
        title: t('gymProfile.loginRequired'),
        description: t('gymProfile.loginRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await trophiesService.unfollowTrophy(slug);
        setIsFollowing(false);
      } else {
        await trophiesService.followTrophy(slug);
        setIsFollowing(true);
      }
    } catch (error) {
      toast({
        title: t('errors.unexpectedError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setFollowLoading(false);
    }
  }, [slug, user, isFollowing, t, toast]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: trophy?.name || t('trophyProfile.share'),
          text: t('trophyProfile.shareText', { name: trophy?.name }),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast({ title: t('gymProfile.linkCopied'), duration: 3000 });
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast({ title: t('errors.shareFailed'), variant: 'destructive' });
      }
    }
  };

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: trophy ? [{ label: trophy.name }] : [],
      loading,
    }),
    [trophy?.name, loading]
  );

  if (!slug) return <Navigate to="/not-found" replace />;

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <Skeleton className="aspect-[16/6] w-full rounded-lg" />
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!trophy) {
    return (
      <div className="mx-auto max-w-4xl">
        <Card>
          <CardContent className="pt-6 text-center">
            <h1 className="text-2xl font-bold text-muted-foreground">
              {t('trophyProfile.notFound')}
            </h1>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* Full-bleed hero, breaking out of the centred page container exactly as
          the gym profile's does. */}
      <Card className="-mx-4 -mt-3 rounded-none border-0 bg-transparent shadow-none sm:-mx-6">
        <CardContent className="p-0">
          <LocationHeader
            locationId={trophy.id}
            name={trophy.name}
            bio={trophy.bio}
            coverImageUrl={trophy.cover_image_url}
            logoUrl={trophy.logo_url}
            logoSmallUrl={trophy.logo_small_url}
            galleryImages={trophy.gallery_images ?? []}
            fallbackIcon={
              <span className="text-lg font-bold">{trophy.name.charAt(0).toUpperCase()}</span>
            }
            isStaffMember={!!isStaffAdmin}
            isStaffAdmin={!!isStaffAdmin}
            hideName
            logoOverlay
            fullBleed
            coverSlideshow
            coverTopRight={
              <>
                {trophy.website_url && (
                  <a
                    href={trophy.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 rounded-md border border-white/20 bg-black/40 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-black/60"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('locationInfo.website')}</span>
                  </a>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleShare}
                  title={t('trophyProfile.share')}
                  className={COVER_OVERLAY_BUTTON}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
                {/* A circuit is run from the admin panel, which is a different
                    host — so this leaves the member app rather than routing.
                    The slug in the path is what opens this circuit there. */}
                {isStaffAdmin && (
                  <a
                    href={`${getGymAdminBaseUrl()}/${trophy.slug}/stages`}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/20 bg-black/40 text-white transition-colors hover:bg-black/60"
                    title={t('trophyProfile.manage')}
                  >
                    <Settings className="h-4 w-4" />
                  </a>
                )}
              </>
            }
            coverBottomRight={
              <Button
                variant="outline"
                size="icon"
                onClick={toggleFollow}
                disabled={followLoading}
                title={
                  isFollowing
                    ? t('gymProfile.removeFromFavorites')
                    : t('gymProfile.addToFavorites')
                }
                className={COVER_OVERLAY_BUTTON}
              >
                {followLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Heart className={`h-4 w-4 ${isFollowing ? 'fill-red-500 text-red-500' : ''}`} />
                )}
              </Button>
            }
          />

          <div className="mx-auto max-w-4xl space-y-2 px-4 pb-6 pt-3 sm:px-6">
            {/* Who backs the circuit, right under its cover. */}
            <SponsorsStrip sponsors={trophy.sponsors ?? []} />

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={trophyBadge(trophy.status).variant}>
                {t(trophyBadge(trophy.status).labelKey)}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {t('trophyProfile.stages', { count: trophy.stages_count })}
              </span>
            </div>

            <p className="text-sm text-muted-foreground">{trophy.full_address}</p>

            {/* An invitation to the circuit's staff is answered here, as a gym's
                is on the gym's page. Confirmed admins have the manage button
                above and need no card. */}
            {currentUserStaff && !isStaffAdmin && (
              <GymStaffActions gymId={trophy.id} gymSlug={trophy.slug} currentUserStaff={currentUserStaff} kind="trophy" />
            )}

            {trophy.participation && (
              <TrophyParticipationBanner participation={trophy.participation} signedIn={!!user} />
            )}

            <FollowersList
              followers={trophy.followers || []}
              totalCount={trophy.followers_count}
              followable={{ kind: 'trophies', slug: trophy.slug }}
            />
          </div>
        </CardContent>
      </Card>

      <div className="mx-auto max-w-4xl space-y-4">
        <TrophyStagesList
          stages={trophy.stages}
          participation={trophy.participation}
          signedIn={!!user}
          onRequestEnter={entry.requestEnter}
          busyStageId={entry.preparing ? entry.pending?.id : null}
          wards={wards}
          onWardsChanged={reloadWards}
        />
      </div>

      <StageEntryDialogs entry={entry} trophyName={trophy.name} />
    </>
  );
}
